<template>
    <div class="form-row form-submit-row" :class="{'flex flex-wrap button-container flex-align-right': isFlex}" v-sticky="floatingEnabled" @click="submitClicked">
        <slot v-bind="{submitClicked, deleteCallback, disableSave}">
            <template v-if="showActionSave">
                <form-input-save v-if="showActionSaveOptions"
                    :class="{'flex--order-10': isFlex}" type="submit"
                    class="submit-form" :disabled="disableSave"/>
                <form-input-submit v-else :class="{'flex--order-10': isFlex}"
                    name="action_save" class="submit-form button button-save" :disabled="disableSave">
                    <i class="material-icons" aria-hidden="true" role="img">save</i> <span>Speichern</span>
                </form-input-submit>
            </template>

            <form-input-submit v-if="showActionApply && viewSize.lgUp" name="action_apply" class="button button-apply" :disabled="disableSave"
                :class="{'flex--order-9': isFlex}">
                <i class="material-icons" aria-hidden="true" v-text="SubmitIcons.save" role="img"></i> <span>Zwischenspeichern</span>
            </form-input-submit>

            <div class="button-separator" :class="{'flex--order-6': isFlex}"/>

            <a v-if="deleteCallback" :class="{'flex--order-5': isFlex}" class="button button-danger" @click="deleteCallback">
                <i class="material-icons" aria-hidden="true" v-text="SubmitIcons.delete" role="img"></i> <span>Löschen</span>
            </a>

            <slot name="custom-button"/>

            <form-input-submit v-if="showActionCancel" name="action_cancel" formnovalidate="formnovalidate" class="button button-cancel spacer-right-15"
                :class="{'flex--order-1': isFlex}">
                <i class="material-icons" aria-hidden="true" v-text="SubmitIcons.close" role="img"></i> <span>Abbrechen</span>
            </form-input-submit>

            <form-input-submit v-if="showActionReset" name="action_reset" formnovalidate="formnovalidate" class="button button-cancel spacer-right-15"
                :class="{'flex--order-1': isFlex}">
                <span>{{actionResetText}}</span>
            </form-input-submit>
        </slot>
    </div>
</template>

<script setup lang="ts">
    import {usePortuna} from '@/composables/portuna';
    import {useRouterUtils} from '@/composables/routerUtils';
    import {useStore} from 'vuex';
    import {SubmitIcons} from '@/library/design';
    import FormInputSave from './FormInputSave.vue';
    import FormInputSubmit from './FormInputSubmit.vue';
    import {computed} from 'vue';

    const portuna = usePortuna();

    const store = useStore<RootState>();

    const viewSize = store.state.viewSize;

    const routerUtils = useRouterUtils();

    const emit = defineEmits<{
        (eventName: 'cancel' | 'submit' | 'reset', target: HTMLElement | HTMLInputElement | HTMLButtonElement): void;
    }>();

    const props = withDefaults(
        defineProps<{
            floating?: boolean;
            noFlex?: boolean;
            redirectOnCancel?: string | object;
            followRef?: boolean;
            deleteCallback?: VoidFunction;
            disableSave?: boolean;
            showActionSave?: boolean;
            showActionSaveOptions?: boolean;
            showActionApply?: boolean;
            showActionCancel?: boolean;
            showActionReset?: boolean;
            actionResetText?: string;
        }>(),
        {
            floating: true,
            noFlex: false,
            followRef: true,
            deleteCallback: undefined,
            disableSave: false,
            showActionSave: true,
            showActionSaveOptions: true,
            showActionApply: true,
            showActionCancel: true,
            showActionReset: false,
            actionResetText: 'Zurücksetzen',
        },
    );

    const isFlex = computed(() =>
    {
        return !props.noFlex;
    });

    const floatingEnabled = computed(() =>
    {
        return props.floating && viewSize.smUp;
    });

    const submitClicked = (event: Event) =>
    {
        let target: HTMLElement | HTMLInputElement | HTMLButtonElement = event.target as HTMLElement;
        // clicked an icon or something
        if (
            target.parentElement &&
            ['input', 'button'].includes(target.parentElement.nodeName.toLowerCase())
        )
        {
            target = target.parentElement;
        }

        event.stopPropagation();

        event.preventDefault();

        if (
            (target instanceof HTMLButtonElement || target instanceof HTMLInputElement) &&
            'submit' === target.type
        )
        {
            let action: 'reset' | 'submit' | 'cancel' = 'submit';
            if (target.name === 'action_cancel')
            {
                action = 'cancel';
            }
            else if (target.name === 'action_reset')
            {
                action = 'reset';
            }

            emit(action, target);

            if (action === 'cancel' && props.redirectOnCancel)
            {
                routerUtils.routerGoBack(props.redirectOnCancel, props.followRef);
            }
        }
    };

    defineExpose(Object.assign({}, portuna, {submitClicked, componentName: 'FormSubmitRow',}));
</script>